/* eslint-disable consistent-return */
import React, {
    ReactElement,
    Ref,
    useCallback,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    ListRenderItemInfo,
    Platform,
    Text,
    TouchableOpacity,
    View,
} from 'react-native';

import { useTranslation } from 'react-i18next';
import { Edge, SafeAreaView } from 'react-native-safe-area-context';
import { useSelector } from 'react-redux';

import {
    BottomSheetFlatList,
    BottomSheetFlatListMethods,
    BottomSheetHandleProps,
    BottomSheetModal,
} from '@gorhom/bottom-sheet';
import { useNavigation } from '@react-navigation/native';

import IconArrowDownSmall from 'assets/images/svgs/ico_arrow_down_small.svg';
import CheckMark from 'assets/images/svgs/ico_checkmark.svg';

import { DETAIL_SEASON_SELECTOR, DETAIL_SEASON_TAB_ID } from 'config/testID';

import { selectScreenReaderEnabled } from 'store/config/selectors';

import useBackHandler from 'utils/hooks/useBackHandler.ts';
import useDebouncedSafeAreaInsets from 'utils/hooks/useDebouncedSafeAreaInsets';
import { heightPercentageToDP, screenHeight } from 'utils/responsiveness';

import {
    SCREEN_DETAIL_PROGRAM_SEASON_TAB_OTHER_EPISODE,
    SCREEN_DETAIL_SEASONS_TAB_SELECTOR,
    SCREEN_DETAIL_SEASON_TAB_SELECTOR,
} from 'screens/Detail/languageConst';

import BottomSheetAccessibleList from 'components/BottomSheetAccessibleList';
import BottomSheetHandle from 'components/BottomSheetHandle';
import SvgButton from 'components/CustomButtons/SvgButton';
import LocalizedText from 'components/LocalizedText';

import { noSeasonName } from '../../constants';
import { SeasonListItem } from '../../types.ts';
import ModalBackdrop from './components/ModalBackdrop';
import styles, { heightItemRow } from './styles';

export interface ISeasonsSelectorNarrow {
    selected: SeasonListItem;
    list: SeasonListItem[];
    onSelect(season: SeasonListItem): void;
}

const edges: Edge[] = ['bottom'];

function keyExtractor(item: SeasonListItem) {
    return item.seasonId.toString();
}

const SeasonBottomSheet = ({
    list,
    selected,
    onSelect,
}: ISeasonsSelectorNarrow): ReactElement => {
    const { t } = useTranslation();
    const navigation = useNavigation();

    const screenReaderEnabled = useSelector(selectScreenReaderEnabled);

    const bottomSheetModalRef = useRef<BottomSheetModal>(undefined);
    const listRef = useRef<BottomSheetFlatListMethods>(undefined);
    const { bottom: bottomInset } = useDebouncedSafeAreaInsets();
    const [isShowBottomSheet, setShowBottomSheet] = useState(false);
    const minListHeight = screenHeight / 2 + bottomInset;
    const prevSnapPoint = useRef(0);
    const hasOneSeason = list.length === 1;

    const handleOpen = useCallback(() => {
        bottomSheetModalRef?.current?.present();
        setShowBottomSheet(true);
    }, []);

    const handleClose = useCallback(() => {
        bottomSheetModalRef.current?.forceClose();
        setShowBottomSheet(false);
    }, []);

    const handleSelect = useCallback(
        (item) => () => {
            onSelect(item);
            setShowBottomSheet(false);
            handleClose();
        },
        [handleClose, onSelect],
    );

    const handleHardwareBackPress = useCallback(() => {
        bottomSheetModalRef.current?.forceClose();
        setShowBottomSheet(false);

        if (Platform.OS === 'android') {
            if (!isShowBottomSheet) {
                navigation.goBack();
            }

            return true;
        }
    }, [isShowBottomSheet, navigation]);

    useBackHandler(handleHardwareBackPress);

    const contentHeight = useMemo(
        () => bottomInset + list.length * heightItemRow,
        [list.length, bottomInset],
    );

    const snapPoints = useMemo(() => {
        if (contentHeight < minListHeight) {
            return [contentHeight];
        }

        if (contentHeight < screenHeight) {
            return [minListHeight, contentHeight];
        }

        return [minListHeight, screenHeight];
    }, [contentHeight, minListHeight]);

    const initialPositionConfig = useMemo(() => {
        const itemIndex = list.findIndex(
            ({ seasonId }) => seasonId === selected.seasonId,
        );
        const selectedItemY = bottomInset + itemIndex * heightItemRow;

        if (minListHeight < selectedItemY) {
            return {
                snapPoint: snapPoints.length - 1,
                itemIndex,
            };
        }

        return {
            snapPoint: 0,
            itemIndex: null,
        };
    }, [minListHeight, snapPoints, list, bottomInset, selected]);

    const handleBottomSheetStateChange = useCallback(
        (idx: number) => {
            const headerHeight = heightPercentageToDP(40);
            const { itemIndex } = initialPositionConfig;
            const isItemVisible =
                contentHeight >=
                (itemIndex ? itemIndex + 1 : 0) * heightItemRow +
                    headerHeight +
                    bottomInset;

            if (
                idx !== -1 &&
                itemIndex &&
                prevSnapPoint.current !== itemIndex &&
                !isItemVisible
            ) {
                listRef.current?.scrollToOffset({
                    offset:
                        (itemIndex + 1) * heightItemRow +
                        headerHeight +
                        bottomInset,
                    animated: false,
                });
            }

            prevSnapPoint.current = idx;
        },
        [bottomInset, initialPositionConfig, contentHeight],
    );

    const renderHandleComponent = useCallback(
        ({ animatedIndex }: BottomSheetHandleProps) => {
            if (snapPoints[snapPoints.length - 1] === screenHeight) {
                return (
                    <BottomSheetHandle
                        title={t(SCREEN_DETAIL_SEASONS_TAB_SELECTOR)}
                        animatedIndex={animatedIndex}
                        onClosePress={handleClose}
                    />
                );
            }

            return null;
        },
        [t, snapPoints, handleClose],
    );

    const renderItem = useCallback(
        ({ item }: ListRenderItemInfo<SeasonListItem>) => {
            const isSelected = selected.seasonId === item.seasonId;

            return (
                <TouchableOpacity
                    key={item.seasonId}
                    style={[
                        styles.listItemContainer,
                        isSelected && styles.listItemContainerSelected,
                    ]}
                    aria-selected={isSelected}
                    onPress={handleSelect(item)}
                    testID={`${DETAIL_SEASON_TAB_ID}_${item.seasonId}`}
                >
                    <Text
                        style={[
                            styles.listItemText,
                            isSelected && styles.listItemTextSelected,
                        ]}
                    >
                        {item.seasonId === noSeasonName ? (
                            <LocalizedText
                                textKey={
                                    SCREEN_DETAIL_PROGRAM_SEASON_TAB_OTHER_EPISODE
                                }
                            />
                        ) : (
                            `${t('SCREEN_DETAIL_SEASON_TAB_SELECTOR')} ${
                                item.seasonNumber
                            }`
                        )}
                    </Text>
                    {isSelected && (
                        <SvgButton
                            accessible={false}
                            accessibilityElementsHidden
                            importantForAccessibility="no-hide-descendants"
                            Svg={CheckMark}
                            onPress={handleClose}
                        />
                    )}
                </TouchableOpacity>
            );
        },
        [selected, handleSelect, t, handleClose],
    );

    return (
        <>
            <View style={styles.container} testID={DETAIL_SEASON_SELECTOR}>
                <TouchableOpacity
                    style={styles.buttonContainer}
                    onPress={handleOpen}
                    disabled={hasOneSeason}
                    accessibilityLabel={`${t(
                        SCREEN_DETAIL_SEASON_TAB_SELECTOR,
                    )} ${
                        selected.seasonId === noSeasonName
                            ? ''
                            : selected.seasonNumber
                    }`}
                    accessibilityRole="button"
                >
                    <Text style={styles.buttonText}>
                        {selected.seasonId === noSeasonName
                            ? t(SCREEN_DETAIL_PROGRAM_SEASON_TAB_OTHER_EPISODE)
                            : `${t(SCREEN_DETAIL_SEASON_TAB_SELECTOR)} ${
                                  selected.seasonNumber
                              }`}
                    </Text>
                    {!hasOneSeason && (
                        <SvgButton
                            accessible={false}
                            importantForAccessibility="no"
                            style={styles.arrowDown}
                            Svg={IconArrowDownSmall}
                            svgStyle={styles.arrowDownSvg}
                            onPress={handleOpen}
                        />
                    )}
                </TouchableOpacity>
            </View>
            {screenReaderEnabled ? (
                <BottomSheetAccessibleList
                    visible={isShowBottomSheet}
                    data={list}
                    renderRowItem={renderItem}
                    keyExtractor={keyExtractor}
                    headerTitle={t(SCREEN_DETAIL_SEASONS_TAB_SELECTOR)}
                    onClose={handleClose}
                />
            ) : (
                <BottomSheetModal
                    enablePanDownToClose
                    ref={
                        bottomSheetModalRef as unknown as Ref<BottomSheetModal>
                    }
                    index={initialPositionConfig.snapPoint}
                    snapPoints={snapPoints}
                    handleComponent={renderHandleComponent}
                    backdropComponent={ModalBackdrop}
                    backgroundStyle={styles.bottomSheetBackgroundStyle}
                    onChange={handleBottomSheetStateChange}
                    onDismiss={handleClose}
                    key={initialPositionConfig.snapPoint}
                >
                    <SafeAreaView edges={edges} style={styles.safeArea}>
                        <BottomSheetFlatList
                            ref={listRef as Ref<BottomSheetFlatListMethods>}
                            keyExtractor={keyExtractor}
                            style={styles.scroll}
                            bounces={false}
                            data={list}
                            renderItem={renderItem}
                        />
                    </SafeAreaView>
                </BottomSheetModal>
            )}
        </>
    );
};

export default SeasonBottomSheet;
